import React from 'react';
import PropTypes from 'prop-types';
import Modal from './Modal';
import { PrimaryButton, SecondaryButton, DangerButton } from '../buttons';
import { WarningIcon, InfoIcon, ErrorIcon, SuccessIcon } from '../../icons';
import { typography, textColors } from '../../tokens';

/**
 * ConfirmDialog Component
 * 
 * Specialized modal for confirmation actions (delete, publish, etc.).
 * Pre-configured with appropriate styling and action buttons.
 * 
 * @example
 * // Basic confirmation
 * const [isOpen, setIsOpen] = useState(false);
 * 
 * <ConfirmDialog
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onConfirm={handleDelete}
 *   title="Delete Item"
 *   message="Are you sure you want to delete this item? This action cannot be undone."
 * />
 * 
 * @example
 * // Danger confirmation (delete)
 * <ConfirmDialog
 *   isOpen={isOpen}
 *   onClose={handleClose}
 *   onConfirm={handleDelete}
 *   title="Delete Website"
 *   message="Are you sure you want to delete this website? This action cannot be undone."
 *   variant="danger"
 *   confirmText="Delete"
 *   cancelText="Cancel"
 * />
 * 
 * @example
 * // Info confirmation
 * <ConfirmDialog
 *   isOpen={isOpen}
 *   onClose={handleClose}
 *   onConfirm={handlePublish}
 *   title="Publish Website"
 *   message="Your website will be publicly visible. Continue?"
 *   variant="info"
 *   confirmText="Publish"
 * />
 */
const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  variant = 'info',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isLoading = false,
  size = 'sm',
  ...props
}) => {
  // Variant configuration
  const variantConfig = {
    info: {
      icon: <InfoIcon size="xl" color="primary" />,
      iconBg: 'bg-blue-100 dark:bg-blue-900/30',
      confirmButton: PrimaryButton,
    },
    warning: {
      icon: <WarningIcon size="xl" color="warning" />,
      iconBg: 'bg-yellow-100 dark:bg-yellow-900/30',
      confirmButton: PrimaryButton,
    },
    danger: {
      icon: <ErrorIcon size="xl" color="danger" />,
      iconBg: 'bg-red-100 dark:bg-red-900/30',
      confirmButton: DangerButton,
    },
    success: {
      icon: <SuccessIcon size="xl" color="success" />,
      iconBg: 'bg-green-100 dark:bg-green-900/30',
      confirmButton: PrimaryButton,
    },
  };
  
  // Be defensive: callers may pass legacy variants like "default" or an undefined value.
  // Fall back to "info" to avoid runtime crashes.
  const normalizedVariant = variant === 'default' ? 'info' : variant;
  const config = variantConfig[normalizedVariant] || variantConfig.info;
  const ConfirmButtonComponent = config.confirmButton || PrimaryButton;
  
  // Handle confirm action
  const handleConfirm = async () => {
    if (onConfirm) {
      await onConfirm();
    }
    onClose();
  };
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={size}
      closeOnOverlayClick={!isLoading}
      closeOnEsc={!isLoading}
      showCloseButton={!isLoading}
      {...props}
    >
      {/* Icon and Content */}
      <div className="text-center">
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className={`w-16 h-16 rounded-full ${config.iconBg} flex items-center justify-center`}>
            {config.icon}
          </div>
        </div>
        
        {/* Title */}
        {title && (
          <h3 className={`${typography.h4} ${textColors.primary} mb-3`}>
            {title}
          </h3>
        )}
        
        {/* Message */}
        {message && (
          <p className={`${typography.body} ${textColors.secondary} mb-6`}>
            {message}
          </p>
        )}
        
        {/* Action Buttons */}
        <div className="flex items-center justify-center space-x-3">
          <SecondaryButton
            onClick={onClose}
            disabled={isLoading}
          >
            {cancelText}
          </SecondaryButton>
          <ConfirmButtonComponent
            onClick={handleConfirm}
            loading={isLoading}
          >
            {confirmText}
          </ConfirmButtonComponent>
        </div>
      </div>
    </Modal>
  );
};

ConfirmDialog.propTypes = {
  /** Whether dialog is open */
  isOpen: PropTypes.bool.isRequired,
  /** Function to call when dialog should close */
  onClose: PropTypes.func.isRequired,
  /** Function to call when user confirms */
  onConfirm: PropTypes.func.isRequired,
  /** Dialog title */
  title: PropTypes.string,
  /** Dialog message/description */
  message: PropTypes.string,
  /** Visual variant (affects icon and button style) */
  variant: PropTypes.oneOf(['info', 'warning', 'danger', 'success']),
  /** Text for confirm button */
  confirmText: PropTypes.string,
  /** Text for cancel button */
  cancelText: PropTypes.string,
  /** Whether action is loading */
  isLoading: PropTypes.bool,
  /** Size variant */
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl', 'full']),
};

export default ConfirmDialog;
